import { Box, Typography } from '@mui/material';
import React, { forwardRef, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import OpenedBoxImage from '../../../assets/icon-openned-box-new.svg';

import {
  HERO_IMAGE_BACKING_COLOR,
  HERO_IMAGE_HEIGHT,
  HERO_IMAGE_HEIGHT_EMBED,
  HERO_IMAGE_MAX_HEIGHT_MOBILE,
  HERO_IMAGE_WIDTH,
  HERO_IMAGE_WIDTH_EMBED,
} from '../../../constants/ui';
import { useBrandingPathname } from '../../../hooks/useBrandingPathname';
import useHeroImage from '../../../hooks/useHeroImage';
import useWindowSize from '../../../hooks/useWindowSize';
import {
  selectBoxImage,
  selectGreetingBody,
  selectGreetingTitle,
  selectHeroSectionBackground,
} from '../../../store/selectors/derived';
import { selectHasExperience, selectIsEmbed } from '../../../store/selectors/init';
import { BackgroundTypeEnum, ImageFitEnum } from '../../../types/shell';
import { sanitizeRichText } from '../../../util/richText';
import { getHeroMinHeight, getPrimaryTextColor } from '../../../util/ui';

import { AppWrapper } from '../_layouts';

export const Hero = forwardRef<HTMLDivElement>((_, ref) => {
  const { mobile } = useWindowSize();
  // The catalog keeps the hero of the page it was opened from, matching the app bar and logo.
  const pathname = useBrandingPathname();

  const boxImageSelector = useMemo(() => selectBoxImage(pathname), [pathname]);
  const heroBackgroundSelector = useMemo(() => selectHeroSectionBackground(pathname), [pathname]);
  const greetingTitleSelector = useMemo(() => selectGreetingTitle(pathname), [pathname]);
  const greetingBodySelector = useMemo(() => selectGreetingBody(pathname), [pathname]);

  const heroBoxImage = useSelector(boxImageSelector);
  const hasExperience = useSelector(selectHasExperience);
  const heroBackground = useSelector(heroBackgroundSelector);
  const rawHeaderText = useSelector(greetingTitleSelector);
  const rawBodyText = useSelector(greetingBodySelector);
  const isEmbed = useSelector(selectIsEmbed);

  // Sender-controlled copy (experience pages, white-label greetings, recipient first name).
  const headerText = useMemo(() => sanitizeRichText(rawHeaderText || ''), [rawHeaderText]);
  const bodyText = useMemo(() => sanitizeRichText(rawBodyText || ''), [rawBodyText]);

  const isBackgroundColor = useMemo(() => heroBackground?.type === BackgroundTypeEnum.Color, [heroBackground?.type]);

  const isBackgroundImage = useMemo(() => heroBackground?.type === BackgroundTypeEnum.Image, [heroBackground?.type]);

  // Only feed a URL when a background image is actually set.
  const { setHeroRef, heroWidth, imageAspect, imageAverageColor } = useHeroImage(
    isBackgroundImage ? heroBackground?.value || null : null,
    ref,
  );

  const [boxImageError, setBoxImageError] = useState(false);

  const bodyTextColor = useMemo(() => {
    const bgColor = isBackgroundImage ? imageAverageColor : heroBackground?.value || null;

    return getPrimaryTextColor(bgColor);
  }, [isBackgroundImage, imageAverageColor, heroBackground?.value]);

  const displayedBody = useMemo(() => {
    if (bodyText) {
      return (
        <Typography
          component="div"
          sx={{
            color: bodyTextColor,
            fontSize: { xs: 16, sm: 20 },
            fontWeight: 'regular',
            lineHeight: 1.4,
          }}
          dangerouslySetInnerHTML={{ __html: bodyText }}
        />
      );
    }

    return null;
  }, [bodyText, bodyTextColor]);

  const isAdapt = useMemo(
    () => isBackgroundImage && heroBackground?.fit === ImageFitEnum.Contain,
    [isBackgroundImage, heroBackground?.fit],
  );

  const boxImage = useMemo(() => {
    if (heroBoxImage) {
      return heroBoxImage;
    }

    if (isEmbed || hasExperience) {
      return null;
    }

    return OpenedBoxImage;
  }, [heroBoxImage, isEmbed, hasExperience]);

  useEffect(() => {
    setBoxImageError(false);
  }, [boxImage]);

  const hasBoxImage = Boolean(boxImage) && !boxImageError;

  const heroContainerStyles = useMemo(() => {
    if (!heroBackground) {
      return {};
    }

    const minHeight = getHeroMinHeight({ isAdapt, heroWidth, imageAspect, mobile });

    const shouldFillColumn = isAdapt || !hasBoxImage;

    if (isBackgroundColor) {
      return {
        minHeight,
        backgroundColor: heroBackground.value,
        ...(shouldFillColumn && { display: 'flex', flexDirection: 'column' as const }),
      };
    }

    if (isBackgroundImage) {
      return {
        minHeight,
        backgroundColor: HERO_IMAGE_BACKING_COLOR,
        backgroundImage: `url('${heroBackground.value}')`,
        backgroundPosition: heroBackground.position,
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
        ...(shouldFillColumn && { display: 'flex', flexDirection: 'column' as const }),
      };
    }

    return {};
  }, [heroBackground, isBackgroundColor, isBackgroundImage, isAdapt, hasBoxImage, mobile, heroWidth, imageAspect]);

  return (
    <Box ref={setHeroRef} data-testid="hero" sx={heroContainerStyles}>
      <AppWrapper
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: { xs: 2, sm: 3, md: 4 },
          py: { xs: 3, sm: 4, md: 6 },
          ...(isAdapt && { flexGrow: 1 }),
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            width: '100%',
          }}
        >
          {headerText && (
            <Typography
              component="div"
              sx={{
                color: bodyTextColor,
                fontSize: { xs: 24, sm: 'clamp(28px, 4vw, 34px)' },
                fontWeight: 'medium',
                lineHeight: 1.3,
              }}
              dangerouslySetInnerHTML={{ __html: headerText }}
            />
          )}
          {displayedBody}
        </Box>
        {hasBoxImage && (
          <Box
            component="img"
            src={boxImage as string}
            alt="campaign box image"
            onError={() => setBoxImageError(true)}
            sx={{
              order: { xs: -1, md: 0 },
              width: { xs: '100%', md: isEmbed ? HERO_IMAGE_WIDTH_EMBED : HERO_IMAGE_WIDTH },
              maxWidth: { xs: HERO_IMAGE_WIDTH, md: 'none' },
              height: { xs: 'auto', md: isEmbed ? HERO_IMAGE_HEIGHT_EMBED : HERO_IMAGE_HEIGHT },
              maxHeight: { xs: HERO_IMAGE_MAX_HEIGHT_MOBILE, md: 'none' },
              flexShrink: 0,
              objectFit: 'contain',
              borderRadius: 0.5,
              alignSelf: isAdapt ? 'center' : { xs: 'center', md: 'start' },
            }}
          />
        )}
      </AppWrapper>
    </Box>
  );
});
